import React, { useState, useEffect, useMemo } from 'react';
import { Clock, User, FileText, Filter, Calendar, Eye, RefreshCw, Search, X } from 'lucide-react';

/**
 * Audit Log Viewer Component
 * Displays audit trail for entities with filtering and search
 */
const AuditLogViewer = ({ entityType, entityId, showFilters = true }) => {
  const [auditLogs, setAuditLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showFilterPanel, setShowFilterPanel] = useState(false);
  const [filters, setFilters] = useState({
    action_category: '',
    performed_by_id: '',
    entity_type: '',
    date_from: '',
    date_to: ''
  });

  const fetchAuditLogs = async () => {
    try {
      setLoading(true);
      const backendUrl = process.env.REACT_APP_BACKEND_URL || import.meta.env.REACT_APP_BACKEND_URL;
      
      let url = `${backendUrl}/api/audit-logs`;
      if (entityType && entityId) {
        url = `${backendUrl}/api/audit-logs/entity/${entityType}/${entityId}`;
      }
      
      const response = await fetch(url);
      const data = await response.json();
      
      if (data.success) {
        setAuditLogs(data.audit_trail || data.audit_logs || []);
      }
    } catch (error) {
      console.error('Error fetching audit logs:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAuditLogs();
  }, [entityType, entityId]);

  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getActionBadge = (action) => {
    const actionColors = {
      'created': 'bg-green-100 text-green-800',
      'updated': 'bg-blue-100 text-blue-800',
      'deleted': 'bg-red-100 text-red-800',
      'status_changed': 'bg-purple-100 text-purple-800',
      'assigned': 'bg-yellow-100 text-yellow-800',
      'submitted': 'bg-indigo-100 text-indigo-800'
    };

    const color = actionColors[action] || 'bg-gray-100 text-gray-800';
    const label = action.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());

    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${color}`}>
        {label}
      </span>
    );
  };

  const getCategoryIcon = (category) => {
    const icons = {
      'create': '➕',
      'update': '✏️',
      'delete': '🗑️',
      'status': '🔄',
      'assignment': '👤'
    };
    return icons[category] || '📝';
  };

  // Multi-field search across audit logs
  const filteredLogs = useMemo(() => {
    let filtered = auditLogs;

    // Apply search term across multiple fields
    if (searchTerm.trim()) {
      const search = searchTerm.toLowerCase();
      filtered = filtered.filter(log => {
        // Search in entity name (candidate name, job title, etc.)
        const entityMatch = log.entity_name?.toLowerCase().includes(search);
        
        // Search in performed by name (recruiter, sales manager)
        const performerMatch = log.performed_by_name?.toLowerCase().includes(search);
        
        // Search in role
        const roleMatch = log.performed_by_role?.toLowerCase().includes(search);
        
        // Search in changes (for client name, job title in change details)
        const changesMatch = log.changes && Object.entries(log.changes).some(([field, change]) => {
          return field.toLowerCase().includes(search) || 
                 change.old?.toString().toLowerCase().includes(search) ||
                 change.new?.toString().toLowerCase().includes(search);
        });
        
        // Search in old/new values
        const valueMatch = log.old_value?.toLowerCase().includes(search) || 
                          log.new_value?.toLowerCase().includes(search);
        
        // Search in field changed
        const fieldMatch = log.field_changed?.toLowerCase().includes(search);
        
        // Search in notes/reason
        const notesMatch = log.notes?.toLowerCase().includes(search) || 
                          log.reason?.toLowerCase().includes(search);

        return entityMatch || performerMatch || roleMatch || changesMatch || 
               valueMatch || fieldMatch || notesMatch;
      });
    }

    // Apply filters
    if (filters.action_category) {
      filtered = filtered.filter(log => log.action_category === filters.action_category);
    }

    if (filters.entity_type) {
      filtered = filtered.filter(log => log.entity_type === filters.entity_type);
    }

    if (filters.date_from) {
      const fromDate = new Date(filters.date_from);
      filtered = filtered.filter(log => new Date(log.timestamp) >= fromDate);
    }

    if (filters.date_to) {
      const toDate = new Date(filters.date_to);
      toDate.setHours(23, 59, 59, 999); // End of day
      filtered = filtered.filter(log => new Date(log.timestamp) <= toDate);
    }

    return filtered;
  }, [auditLogs, searchTerm, filters]);

  // Get unique values for filters
  const uniqueCategories = useMemo(() => {
    return [...new Set(auditLogs.map(log => log.action_category).filter(Boolean))];
  }, [auditLogs]);

  const uniqueEntityTypes = useMemo(() => {
    return [...new Set(auditLogs.map(log => log.entity_type).filter(Boolean))];
  }, [auditLogs]);

  const clearFilters = () => {
    setSearchTerm('');
    setFilters({
      action_category: '',
      performed_by_id: '',
      entity_type: '',
      date_from: '',
      date_to: ''
    });
  };

  const hasActiveFilters = searchTerm || filters.action_category || filters.entity_type || 
                          filters.date_from || filters.date_to;

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
        <p className="mt-4 text-gray-600">Loading audit logs...</p>
      </div>
    );
  }

  if (auditLogs.length === 0 && !loading) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center">
        <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
        <p className="text-gray-600">No audit history available</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header with Search */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <Clock className="h-5 w-5 text-blue-600" />
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Audit Trail</h3>
              <p className="text-sm text-gray-600">
                Showing {filteredLogs.length} of {auditLogs.length} changes
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowFilterPanel(!showFilterPanel)}
              className={`flex items-center gap-2 px-3 py-2 text-sm rounded-lg transition-colors ${
                showFilterPanel || hasActiveFilters
                  ? 'bg-blue-100 text-blue-700'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              <Filter className="h-4 w-4" />
              Filters
              {hasActiveFilters && (
                <span className="px-1.5 py-0.5 bg-blue-600 text-white text-xs rounded-full">
                  •
                </span>
              )}
            </button>
            <button
              onClick={fetchAuditLogs}
              className="flex items-center gap-2 px-3 py-2 text-sm text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
            >
              <RefreshCw className="h-4 w-4" />
              Refresh
            </button>
          </div>
        </div>

        {/* Search Bar */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
          <input
            type="text"
            placeholder="Search by candidate name, recruiter, sales manager, client, job title..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-10 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
          {searchTerm && (
            <button
              onClick={() => setSearchTerm('')}
              className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
            >
              <X className="h-5 w-5" />
            </button>
          )}
        </div>

        {/* Filter Panel */}
        {showFilterPanel && (
          <div className="mt-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
            <div className="flex items-center justify-between mb-3">
              <h4 className="text-sm font-semibold text-gray-900">Advanced Filters</h4>
              {hasActiveFilters && (
                <button
                  onClick={clearFilters}
                  className="text-xs text-blue-600 hover:text-blue-700 font-medium"
                >
                  Clear All
                </button>
              )}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
              {/* Action Category Filter */}
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Action Type
                </label>
                <select
                  value={filters.action_category}
                  onChange={(e) => setFilters({ ...filters, action_category: e.target.value })}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">All Actions</option>
                  {uniqueCategories.map(cat => (
                    <option key={cat} value={cat}>
                      {cat.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                    </option>
                  ))}
                </select>
              </div>

              {/* Entity Type Filter */}
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Entity Type
                </label>
                <select
                  value={filters.entity_type}
                  onChange={(e) => setFilters({ ...filters, entity_type: e.target.value })}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">All Types</option>
                  {uniqueEntityTypes.map(type => (
                    <option key={type} value={type}>
                      {type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                    </option>
                  ))}
                </select>
              </div>

              {/* Date From */}
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  From Date
                </label>
                <input
                  type="date"
                  value={filters.date_from}
                  onChange={(e) => setFilters({ ...filters, date_from: e.target.value })}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>

              {/* Date To */}
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  To Date
                </label>
                <input
                  type="date"
                  value={filters.date_to}
                  onChange={(e) => setFilters({ ...filters, date_to: e.target.value })}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
          </div>
        )}

        {/* Active Search/Filter Indicator */}
        {hasActiveFilters && (
          <div className="mt-3 flex items-center gap-2 text-sm text-gray-600">
            <span className="font-medium">Active filters:</span>
            {searchTerm && (
              <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded">
                Search: "{searchTerm}"
              </span>
            )}
            {filters.action_category && (
              <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded">
                {filters.action_category.replace(/_/g, ' ')}
              </span>
            )}
            {filters.entity_type && (
              <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded">
                {filters.entity_type}
              </span>
            )}
            {(filters.date_from || filters.date_to) && (
              <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded">
                Date range
              </span>
            )}
          </div>
        )}
      </div>

      {/* Timeline */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        {filteredLogs.length === 0 ? (
          <div className="p-8 text-center">
            <Search className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-600">No matching audit logs found</p>
            {hasActiveFilters && (
              <button
                onClick={clearFilters}
                className="mt-3 text-sm text-blue-600 hover:text-blue-700 font-medium"
              >
                Clear filters to show all logs
              </button>
            )}
          </div>
        ) : (
          <div className="p-6">
            <div className="space-y-4">
              {filteredLogs.map((log, index) => (
              <div key={log.id || index} className="relative">
                {/* Timeline connector */}
                {index < filteredLogs.length - 1 && (
                  <div className="absolute left-6 top-12 bottom-0 w-0.5 bg-gray-200"></div>
                )}
                
                {/* Log Entry */}
                <div className="flex gap-4">
                  {/* Icon */}
                  <div className="flex-shrink-0">
                    <div className="h-12 w-12 rounded-full bg-blue-100 flex items-center justify-center text-xl">
                      {getCategoryIcon(log.action_category)}
                    </div>
                  </div>

                  {/* Content */}
                  <div className="flex-1 bg-gray-50 rounded-lg p-4">
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex items-center gap-2">
                        {getActionBadge(log.action)}
                        <span className="text-xs text-gray-500">
                          {log.entity_type && (
                            <span className="px-2 py-0.5 bg-white border border-gray-200 rounded">
                              {log.entity_type}
                            </span>
                          )}
                        </span>
                      </div>
                      <div className="flex items-center gap-1 text-xs text-gray-500">
                        <Calendar className="h-3 w-3" />
                        {formatTimestamp(log.timestamp)}
                      </div>
                    </div>

                    {/* Entity Name */}
                    {log.entity_name && (
                      <div className="font-medium text-gray-900 mb-2">
                        {log.entity_name}
                      </div>
                    )}

                    {/* Change Details */}
                    {log.old_value && log.new_value && (
                      <div className="mb-2 text-sm">
                        <span className="text-gray-600">
                          {log.field_changed && `${log.field_changed}: `}
                          <span className="line-through text-red-600">{log.old_value}</span>
                          {' → '}
                          <span className="text-green-600 font-medium">{log.new_value}</span>
                        </span>
                      </div>
                    )}

                    {/* Multiple Changes */}
                    {log.changes && Object.keys(log.changes).length > 0 && (
                      <div className="mb-2 text-sm space-y-1">
                        {Object.entries(log.changes).map(([field, change]) => (
                          <div key={field} className="text-gray-600">
                            <span className="font-medium">{field}:</span>{' '}
                            <span className="line-through text-red-600">{change.old}</span>
                            {' → '}
                            <span className="text-green-600">{change.new}</span>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Reason */}
                    {log.reason && (
                      <div className="mb-2 text-sm text-gray-600 italic">
                        Reason: {log.reason}
                      </div>
                    )}

                    {/* Notes */}
                    {log.notes && (
                      <div className="mb-2 text-sm text-gray-600">
                        Note: {log.notes}
                      </div>
                    )}

                    {/* Performed By */}
                    <div className="flex items-center gap-2 text-sm text-gray-500 pt-2 border-t border-gray-200">
                      <User className="h-3 w-3" />
                      <span className="font-medium">{log.performed_by_name}</span>
                      {log.performed_by_role && (
                        <span className="text-xs">({log.performed_by_role})</span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AuditLogViewer;
